import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@material-ui/core';
import { useFormik } from "formik";
import * as Yup from "yup";
import Immutable from "immutable";
import { useDispatch } from 'react-redux';
import { addCustomItems, updateCustomItems } from "../../../redux/reducers/inventory/inventoryReducer";

const CustomItemModal = ({ show, onSubmit, onClose, editData }) => {
    const dispatch = useDispatch();

    const initialValues = {
        item_name: "",
        item_qty: "",
        item_unit: "feet",
        item_length: "",
        item_width: "",
        item_height: "",
    };

    const CustomItemSchema = Yup.object().shape({
        item_name: Yup.string().required("Required"),
        item_qty: Yup.number()
            .required("Required")
            .min(1, "Must be at least 1")
            .integer("Please enter only whole numbers"),
        item_unit: Yup.string().required("Required"),
        item_length: Yup.number()
            .required("Required")
            .integer("Please enter only whole numbers")
            .positive("Value must be greater than zero"),
        item_width: Yup.number()
            .required("Required")
            .integer("Please enter only whole numbers")
            .positive("Value must be greater than zero"),
        item_height: Yup.number()
            .required("Required")
            .integer("Please enter only whole numbers")
            .positive("Value must be greater than zero"),
    });

    const CustomItemForm = useFormik({
        initialValues: initialValues,
        validationSchema: CustomItemSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                const volume_cuft = Number(values.item_length) * Number(values.item_width) * Number(values.item_height);
                const area_sqft = Number(values.item_length) * Number(values.item_width);
                if (!editData) {
                    const params = {
                        id: Math.random().toString(36).substr(2, 9),
                        item_name: values.item_name,
                        item_qty: values.item_qty.toString(),
                        item_length: values.item_length.toString(),
                        item_width: values.item_width.toString(),
                        item_height: values.item_height.toString(),
                        item_description: "Custom Item",
                        item_volume_cuft: volume_cuft,
                        item_area_sqft: area_sqft,
                        total_item_volume_cuft: Number((volume_cuft * Number(values.item_qty)).toFixed(2)),
                        total_item_area_sqft: Number((area_sqft * Number(values.item_qty)).toFixed(2))
                    };
                    dispatch(addCustomItems(Immutable.fromJS(params)));
                } else {
                    const params = {
                        id: editData.id,
                        item_name: values.item_name,
                        item_qty: values.item_qty.toString(),
                        item_length: values.item_length.toString(),
                        item_width: values.item_width.toString(),
                        item_height: values.item_height.toString(),
                        item_description: editData.item_description,
                        item_volume_cuft: volume_cuft,
                        item_area_sqft: area_sqft,
                        total_item_volume_cuft: Number((volume_cuft * Number(values.item_qty)).toFixed(2)),
                        total_item_area_sqft: Number((area_sqft * Number(values.item_qty)).toFixed(2))
                    };
                    dispatch(updateCustomItems(Immutable.fromJS(params)));
                }
                onSubmit(true);
                resetForm();
            } catch (e) {
                console.log("error", e);
            }
        },
    });

    const onHideModal = () => {
        onClose(true);
        CustomItemForm.resetForm();
    };

    const editFormFields = () => {
        if (editData) {
            CustomItemForm.setFieldValue("item_name", editData.item_name);
            CustomItemForm.setFieldValue("item_qty", editData.item_qty);
            CustomItemForm.setFieldValue("item_unit", "feet");
            CustomItemForm.setFieldValue("item_length", editData.item_length);
            CustomItemForm.setFieldValue("item_width", editData.item_width);
            CustomItemForm.setFieldValue("item_height", editData.item_height);
        }
    };

    return (
        <Dialog
            fullWidth={true}
            open={show}
            onClose={onHideModal}
            disableEscapeKeyDown
            TransitionProps={{ onEntered: editFormFields }}
        >
            <DialogTitle style={{ padding: "16px 24px" }} disableTypography>
                <Typography className="font-weight-bold" variant="subtitle1" color="textPrimary">
                    {editData ? "Edit Custom Item" : "Add Custom Item"}
                </Typography>
            </DialogTitle>
            <DialogContent style={{ padding: "16px 24px" }}>
                <form onSubmit={CustomItemForm.handleSubmit} noValidate autoComplete="off">
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="text"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Item Name"
                                placeholder="Item Name"
                                name="item_name"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_name}
                                helperText={CustomItemForm.touched.item_name && CustomItemForm.errors.item_name}
                                error={Boolean(CustomItemForm.touched.item_name && CustomItemForm.errors.item_name)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="number"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Quantity"
                                placeholder="Quantity"
                                name="item_qty"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_qty}
                                helperText={CustomItemForm.touched.item_qty && CustomItemForm.errors.item_qty}
                                error={Boolean(CustomItemForm.touched.item_qty && CustomItemForm.errors.item_qty)}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="text"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Unit"
                                placeholder="Unit"
                                name="item_unit"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_unit}
                                helperText={CustomItemForm.touched.item_unit && CustomItemForm.errors.item_unit}
                                error={Boolean(CustomItemForm.touched.item_unit && CustomItemForm.errors.item_unit)}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="number"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Length"
                                placeholder="Length"
                                name="item_length"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_length}
                                helperText={CustomItemForm.touched.item_length && CustomItemForm.errors.item_length}
                                error={Boolean(CustomItemForm.touched.item_length && CustomItemForm.errors.item_length)}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="number"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Width"
                                placeholder="Width"
                                name="item_width"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_width}
                                helperText={CustomItemForm.touched.item_width && CustomItemForm.errors.item_width}
                                error={Boolean(CustomItemForm.touched.item_width && CustomItemForm.errors.item_width)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={6}>
                            <TextField
                                type="number"
                                variant="outlined"
                                color="primary"
                                margin="dense"
                                size="small"
                                fullWidth
                                label="Height"
                                placeholder="Height"
                                name="item_height"
                                onBlur={CustomItemForm.handleBlur}
                                onChange={CustomItemForm.handleChange}
                                value={CustomItemForm.values.item_height}
                                helperText={CustomItemForm.touched.item_height && CustomItemForm.errors.item_height}
                                error={Boolean(CustomItemForm.touched.item_height && CustomItemForm.errors.item_height)}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions style={{ padding: "16px 24px" }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onHideModal}
                >
                    Close
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={CustomItemForm.isSubmitting}
                    onClick={CustomItemForm.submitForm}
                >
                    {editData ? "Update" : "Submit"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomItemModal;